// The clip a clipping frame makes for what it holds, as both renderers' scene
// walks carry it down -- the engine's `render::clip::Clip`.
//
// A clip was a rectangle on the screen: the frame's box taken as its bounding
// rectangle there and handed to each draw as its scissor. That is the box
// exactly while the frame stands upright; turned, the box is a quadrilateral
// the rectangle round it holds with room to spare, and what the frame holds
// showed past its corners. So a clip is two things: the scissor, exact for an
// upright frame and bounding the rest, and -- from the first clipping frame
// that does not stand upright -- that frame's box in its own units, narrowed
// by every clipping frame inside it taken into the same plane. Each layer is
// handed that box in its own units (`clipLocal`), and the shader and the CPU
// painter cut at it for each pixel (`layer_paint::clip_coverage`).
//
// The web's layouts are affine (it has no world to hang a document in), so a
// box is taken from one frame to another without the projective divide.

import type { Mat4, Rect } from './layoutMath.js';

/** `[x0, y0, x1, y1]` in some frame's own units. */
export type ClipBox = [number, number, number, number];

/** A rectangle on the screen, in pixels. */
export interface ClipRect { x0: number; y0: number; x1: number; y1: number; }

/**
 * No clip in a layer's own units: a rectangle turned inside out, which the
 * engine reads as none by `x0 > x1` (`layer_paint::NO_LOCAL_CLIP`).
 */
export const NO_LOCAL_CLIP: ClipBox = [0, 0, -1, -1];

/** A clip that leaves nothing: every pixel is outside it. Not `NO_LOCAL_CLIP`. */
const NOTHING_LEFT: ClipBox = [1e30, 1e30, 1e30, 1e30];

interface PlaneClip {
  fromLocal: Mat4;
  rect: ClipBox;
  toLocal: Mat4;
}

/** What the frames above a node clip it to. */
export interface SceneClip {
  /** Every clipping frame's bounding rectangle on the screen, intersected. */
  readonly scissor: ClipRect | null;
  /** From the first clipping frame not upright on the screen: its plane and what is left of its box. */
  readonly plane: PlaneClip | null;
}

export const NO_CLIP: SceneClip = { scissor: null, plane: null };

/** The part of a frame's layout a clip is made from. */
export interface ClippingFrameLayout {
  fromLocal: Mat4;
  toLocal: Mat4;
  localBounds: Rect;
  aabb: Rect;
}

/** The clip inside a clipping frame laid out as `frame`. */
export function clipWithin(clip: SceneClip, frame: ClippingFrameLayout): SceneClip {
  const { x, y, w, h } = frame.aabb;
  const onScreen: ClipRect = { x0: x, y0: y, x1: x + w, y1: y + h };
  const scissor = clip.scissor
    ? {
      x0: Math.max(clip.scissor.x0, onScreen.x0),
      y0: Math.max(clip.scissor.y0, onScreen.y0),
      x1: Math.min(clip.scissor.x1, onScreen.x1),
      y1: Math.min(clip.scissor.y1, onScreen.y1),
    }
    : onScreen;
  const bounds = frame.localBounds;
  const own: ClipBox = [bounds.x, bounds.y, bounds.x + bounds.w, bounds.y + bounds.h];
  let plane: PlaneClip | null;
  if (clip.plane) {
    // Exact for a frame upright in the plane, its bounding rectangle there for one turned.
    const taken = through(frame.fromLocal, clip.plane.toLocal, own);
    plane = { ...clip.plane, rect: taken ? intersect(clip.plane.rect, taken) : clip.plane.rect };
  } else if (standsUpright(frame.fromLocal)) {
    plane = null;
  } else {
    plane = { fromLocal: frame.fromLocal, toLocal: frame.toLocal, rect: own };
  }
  return { scissor, plane };
}

/**
 * The clip in the units of a layer seen through `layerToLocal`, as the
 * uniform block's and the painter's `clipLocal` take it: `NO_LOCAL_CLIP`
 * where the scissor holds the whole of it.
 */
export function clipInLayer(clip: SceneClip, layerToLocal: Mat4): ClipBox {
  const plane = clip.plane;
  if (!plane) return NO_LOCAL_CLIP;
  if (plane.rect[0] > plane.rect[2] || plane.rect[1] > plane.rect[3]) return NOTHING_LEFT;
  return through(plane.fromLocal, layerToLocal, plane.rect) ?? NO_LOCAL_CLIP;
}

/** Whether a box under `fromLocal` lands on the screen as an upright rectangle. */
function standsUpright(fromLocal: Mat4): boolean {
  return fromLocal[1] === 0 && fromLocal[4] === 0 && fromLocal[3] === 0 && fromLocal[7] === 0;
}

/** The bounding rectangle of `rect`'s corners taken through `from` and then `to`. */
function through(from: Mat4, to: Mat4, rect: ClipBox): ClipBox | null {
  const [x0, y0, x1, y1] = rect;
  const out: ClipBox = [Infinity, Infinity, -Infinity, -Infinity];
  for (const [cx, cy] of [[x0, y0], [x1, y0], [x1, y1], [x0, y1]]) {
    const sx = from[0] * cx + from[4] * cy + from[12];
    const sy = from[1] * cx + from[5] * cy + from[13];
    const lx = to[0] * sx + to[4] * sy + to[12];
    const ly = to[1] * sx + to[5] * sy + to[13];
    out[0] = Math.min(out[0], lx);
    out[1] = Math.min(out[1], ly);
    out[2] = Math.max(out[2], lx);
    out[3] = Math.max(out[3], ly);
  }
  return out.every(Number.isFinite) ? out : null;
}

function intersect(a: ClipBox, b: ClipBox): ClipBox {
  return [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.min(a[2], b[2]), Math.min(a[3], b[3])];
}
